import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HEADLIGHT_FILMS } from "../data";
import { useI18n } from "../i18n";

const Card = ({ film, i, t, onQuote }) => (
  <motion.div
    data-testid={`headlight-card-${film.id}`}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0F1115] hover:border-cyan-400/40 transition-colors duration-500"
  >
    <div className="relative overflow-hidden aspect-[4/5]">
      <img
        src={film.image}
        alt={t(`headlights.items.${film.id}.name`)}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] via-transparent to-transparent opacity-90" />
      <span
        className="absolute top-4 left-4 flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.2em] bg-black/50 backdrop-blur px-3 py-1.5 rounded-full border border-white/10"
      >
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: film.accent, boxShadow: `0 0 10px ${film.accent}` }} />
        {t(`headlights.items.${film.id}.tag`)}
      </span>
    </div>

    <div className="p-6 flex flex-col flex-1">
      <h3 className="font-display font-600 uppercase tracking-tight text-lg leading-tight group-hover:text-cyan transition-colors duration-300">
        {t(`headlights.items.${film.id}.name`)}
      </h3>
      <p className="text-neutral-400 text-sm font-light mt-2 flex-1">
        {t(`headlights.items.${film.id}.desc`)}
      </p>
      <button
        data-testid={`headlight-cta-${film.id}`}
        onClick={onQuote}
        className="mt-5 inline-flex items-center justify-between gap-2 text-sm font-medium text-cyan border border-cyan-400/30 rounded-xl px-4 py-3 hover:bg-cyan-400 hover:text-black transition-colors duration-300"
      >
        {t("headlights.cta")}
        <ArrowUpRight size={16} />
      </button>
    </div>
  </motion.div>
);

export const HeadlightFilms = ({ onQuote }) => {
  const { t } = useI18n();
  return (
    <section id="films" data-testid="headlights-section" className="relative py-28 md:py-36 px-6 md:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="font-mono-accent text-xs uppercase tracking-[0.3em] text-cyan">{t("headlights.tag")}</span>
            <h2 className="font-display font-700 uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-none">
              {t("headlights.title1")} <span className="text-cyan neon-text-glow">{t("headlights.title2")}</span>
            </h2>
          </div>
          <p className="max-w-sm text-neutral-400 font-light">{t("headlights.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HEADLIGHT_FILMS.map((film, i) => (
            <Card key={film.id} film={film} i={i} t={t} onQuote={onQuote} />
          ))}
        </div>
      </div>
    </section>
  );
};
